import { useState, useRef, useEffect } from "react";
import { getVisibleTopics, matchTopics } from "./helpContent";

const B = {
  red:"#A51C30", black:"#141414", charcoal:"#222222",
  gray:"#4A4A4A", silver:"#8A8A8A", light:"#E8E8E8", offWhite:"#F5F4F2", white:"#FFFFFF",
  teal:"#0891B2",
};

// Purely scripted/keyword-matched — no AI, no API calls, no access to the
// user's live data. See src/helpContent.js for the actual guide content.
export default function HelpChatbot({ currentUser, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {from:'user'|'bot', text, suggestions?}
  const [input, setInput] = useState("");
  const scrollRef = useRef();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  if (!currentUser) return null;
  const role = currentUser.role;
  const topics = getVisibleTopics(role);

  function askTopic(topic) {
    setMessages(prev => [
      ...prev,
      { from: "user", text: topic.question },
      { from: "bot", text: topic.answer, action: topic.action },
    ]);
  }

  function handleSend() {
    const q = input.trim();
    if (!q) return;
    setInput("");
    const matches = matchTopics(q, role);
    if (matches.length === 0) {
      setMessages(prev => [
        ...prev,
        { from: "user", text: q },
        { from: "bot", text: "I'm not sure about that one — here are some things I can help with:", suggestions: topics.slice(0, 6) },
      ]);
    } else if (matches.length === 1 || matches[0].question === matches[1]?.question) {
      setMessages(prev => [...prev, { from: "user", text: q }, { from: "bot", text: matches[0].answer, action: matches[0].action }]);
    } else {
      setMessages(prev => [
        ...prev,
        { from: "user", text: q },
        { from: "bot", text: "Did you mean one of these?", suggestions: matches.slice(0, 4) },
      ]);
    }
  }

  function reset() {
    setMessages([]);
  }

  const quickTopics = topics.slice(0, 6);

  return (
    <>
      <button onClick={() => setOpen(o => !o)}
        style={{position:"fixed",bottom:24,right:24,width:56,height:56,borderRadius:"50%",border:"none",
          background:B.red,color:B.white,fontSize:24,cursor:"pointer",boxShadow:"0 4px 16px rgba(0,0,0,0.3)",
          zIndex:1300,display:"flex",alignItems:"center",justifyContent:"center"}}
        aria-label="Help">
        {open ? "✕" : "💬"}
      </button>

      {open && (
        <div style={{position:"fixed",bottom:92,right:24,width:"min(360px,calc(100vw - 32px))",maxHeight:"min(560px,calc(100vh - 140px))",
          background:B.white,borderRadius:14,boxShadow:"0 12px 40px rgba(0,0,0,0.25)",display:"flex",flexDirection:"column",
          zIndex:1300,overflow:"hidden",border:`1px solid ${B.light}`,fontFamily:"'DM Sans',sans-serif"}}>

          <div style={{padding:"14px 16px",background:B.black,display:"flex",justifyContent:"space-between",alignItems:"center",flexShrink:0}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:B.white}}>Help Guide</div>
              <div style={{fontSize:10,color:B.silver}}>Ask how to do something</div>
            </div>
            {messages.length > 0 && (
              <button onClick={reset} style={{background:"none",border:"none",color:B.silver,fontSize:11,cursor:"pointer",fontFamily:"'DM Sans',sans-serif"}}>Reset</button>
            )}
          </div>

          <div ref={scrollRef} style={{flex:1,overflowY:"auto",padding:"14px 16px",display:"flex",flexDirection:"column",gap:10,minHeight:200}}>
            {messages.length === 0 && (
              <>
                <div style={{fontSize:12,color:B.gray,marginBottom:4}}>👋 Hi! Ask me a question, or pick one below:</div>
                {quickTopics.map(t => (
                  <button key={t.id} onClick={() => askTopic(t)}
                    style={{textAlign:"left",padding:"8px 12px",borderRadius:8,border:`1px solid ${B.light}`,background:B.offWhite,
                      cursor:"pointer",fontSize:12,color:B.black,fontFamily:"'DM Sans',sans-serif"}}>
                    {t.question}
                  </button>
                ))}
              </>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{alignSelf:m.from==="user"?"flex-end":"flex-start",maxWidth:"88%"}}>
                <div style={{padding:"9px 12px",borderRadius:10,fontSize:12.5,lineHeight:1.45,
                  background:m.from==="user"?B.red:B.offWhite,color:m.from==="user"?B.white:B.black,
                  border:m.from==="user"?"none":`1px solid ${B.light}`}}>
                  {m.text}
                </div>
                {m.action && (
                  <button onClick={() => { onNavigate?.(m.action.view); setOpen(false); }}
                    style={{marginTop:6,padding:"6px 12px",borderRadius:8,border:"none",background:B.teal,color:B.white,
                      cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}>
                    {m.action.label} →
                  </button>
                )}
                {m.suggestions && (
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginTop:6}}>
                    {m.suggestions.map(t => (
                      <button key={t.id} onClick={() => askTopic(t)}
                        style={{textAlign:"left",padding:"7px 11px",borderRadius:8,border:`1px solid ${B.light}`,background:B.white,
                          cursor:"pointer",fontSize:11.5,color:B.black,fontFamily:"'DM Sans',sans-serif"}}>
                        {t.question}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{padding:"10px 12px",borderTop:`1px solid ${B.light}`,display:"flex",gap:8,flexShrink:0}}>
            <input value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleSend(); }}
              placeholder="Ask a question…"
              style={{flex:1,padding:"9px 12px",borderRadius:8,border:`1px solid ${B.light}`,fontSize:12,
                fontFamily:"'DM Sans',sans-serif",outline:"none",color:B.black}}/>
            <button onClick={handleSend}
              style={{padding:"9px 14px",borderRadius:8,border:"none",background:B.red,color:B.white,
                cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'DM Sans',sans-serif"}}>
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
}
